import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, RotateCcw, SkipForward, Volume2, Minimize2, Lock, Clock, Timer as TimerIcon } from 'lucide-react';
import { useTasks, useTimerStore } from '@flowfocus/core';
import { useTimer } from '../hooks/TimerContext';
import { SESSION } from '../pages/TimerPage';
import { WhiteNoisePanel } from './WhiteNoisePanel';

/** Full-screen study mode — big minimalist timer/clock, great in landscape on
 *  mobile, keeps the screen awake (Wake Lock). */
export function FocusOverlay({ uid }: { uid: string }) {
  const t = useTimer();
  const settings = useTimerStore((s) => s.settings);
  const { tasks } = useTasks(uid);
  const [showNoise, setShowNoise] = useState(false);
  const [mode, setMode] = useState<'timer' | 'clock'>('timer');
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [now, setNow] = useState(() => new Date());

  // Keep the screen awake while studying.
  const wakeRef = useRef<any>(null);
  useEffect(() => {
    let released = false;
    const acquire = async () => {
      try { wakeRef.current = await (navigator as any).wakeLock?.request('screen'); } catch { /* unsupported */ }
    };
    acquire();
    const onVis = () => { if (document.visibilityState === 'visible' && !released) acquire(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVis);
      try { wakeRef.current?.release?.(); } catch { /* */ }
    };
  }, []);

  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Live clock tick (only matters in clock mode, cheap).
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const running = t.status === 'running';
  const paused = t.status === 'paused';
  const session = SESSION[t.sessionType];
  const strictLock = settings.strictMode && running;
  const activeTask = tasks.find((x) => x.id === t.taskId);
  const noiseOn = settings.whiteNoise.enabled && Object.values(settings.whiteNoise.sounds).some((v) => v > 0);

  const landscape = vp.w > vp.h;
  const ring = Math.max(190, Math.min(vp.w, vp.h) * (landscape ? 0.66 : 0.8));
  const R = ring / 2 - 12, C = ring / 2, circ = 2 * Math.PI * R;
  const offset = circ * (1 - t.progress);
  const onPlay = () => (running ? t.pause() : paused ? t.resume() : t.start(t.taskId));

  const clockStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--bg-app)', color: 'var(--text-pri)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, animation: 'fadeIn 220ms ease' }}>
      {/* top-left: sounds */}
      <button onClick={() => setShowNoise((v) => !v)} title="Sonidos" style={topBtn(noiseOn ? session.color : 'var(--fill)', noiseOn ? '#fff' : 'var(--text-sec)', 'left')}><Volume2 size={18} /></button>
      {/* top-right: mode toggle + close */}
      <div style={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 8 }}>
        <button onClick={() => setMode((m) => (m === 'timer' ? 'clock' : 'timer'))} title={mode === 'timer' ? 'Ver reloj' : 'Ver temporizador'} style={topBtn('var(--fill)', 'var(--text-sec)')}>
          {mode === 'timer' ? <Clock size={18} /> : <TimerIcon size={18} />}
        </button>
        <button onClick={t.closeFullscreen} title="Salir" style={topBtn('var(--fill)', 'var(--text-sec)')}><Minimize2 size={18} /></button>
      </div>

      {mode === 'clock' ? (
        // ── Minimalist clock ──────────────────────────────────────────────
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: 'clamp(80px, 26vw, 360px)', fontWeight: 200, letterSpacing: '-4px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{clockStr}</span>
          {running && (
            <span style={{ marginTop: 18, fontSize: 'clamp(16px, 4vw, 26px)', color: session.color, fontWeight: 600, letterSpacing: '0.04em' }}>
              {session.label} · {t.display}
            </span>
          )}
          {activeTask && <span style={{ marginTop: 10, color: 'var(--text-mut)', fontSize: 'clamp(13px,3vw,18px)' }}>{activeTask.name}</span>}
        </div>
      ) : (
        // ── Timer ─────────────────────────────────────────────────────────
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: landscape ? 18 : 34, opacity: running ? 0.4 : 1, pointerEvents: running ? 'none' : 'auto' }}>
            {(['work', 'shortBreak', 'longBreak'] as const).map((s) => (
              <button key={s} onClick={() => t.setSession(s)} style={{ border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '8px 16px', borderRadius: 22, background: t.sessionType === s ? 'var(--fill)' : 'transparent', color: t.sessionType === s ? 'var(--text-pri)' : 'var(--text-mut)' }}>{SESSION[s].label}</button>
            ))}
          </div>

          <div style={{ position: 'relative', width: ring, height: ring }}>
            <svg width={ring} height={ring} viewBox={`0 0 ${ring} ${ring}`}>
              <circle cx={C} cy={C} r={R} fill="none" stroke="var(--fill)" strokeWidth={11} />
              <circle cx={C} cy={C} r={R} fill="none" stroke={session.color} strokeWidth={11} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} transform={`rotate(-90 ${C} ${C})`} style={{ transition: 'stroke-dashoffset 0.5s linear' }} />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: `${ring * 0.26}px`, fontWeight: 200, letterSpacing: '-2px', fontVariantNumeric: 'tabular-nums' }}>{t.display}</span>
              <span style={{ fontSize: `${Math.max(11, ring * 0.045)}px`, fontWeight: 700, color: session.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>{running ? 'En curso' : paused ? 'En pausa' : session.label}</span>
            </div>
          </div>

          <div style={{ marginTop: 22, marginBottom: 26, minHeight: 22, color: 'var(--text-mut)', fontSize: 16 }}>{activeTask ? activeTask.name : 'Sin tarea seleccionada'}</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 34 }}>
            <button onClick={t.reset} disabled={strictLock} style={{ ...ctrl(), opacity: strictLock ? 0.25 : 1 }} title="Reiniciar"><RotateCcw size={22} /></button>
            <button onClick={onPlay} style={{ width: 88, height: 88, borderRadius: '50%', border: 'none', cursor: 'pointer', background: session.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 12px 32px ${session.color === 'var(--primary)' ? 'rgba(10,23,48,0.4)' : 'rgba(0,0,0,0.25)'}` }}>
              {running ? <Pause size={36} fill="#fff" /> : <Play size={36} fill="#fff" style={{ marginLeft: 4 }} />}
            </button>
            <button onClick={t.skip} disabled={strictLock} style={{ ...ctrl(), opacity: strictLock ? 0.25 : 1 }} title="Saltar"><SkipForward size={22} /></button>
          </div>

          {strictLock && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 22, color: 'var(--text-mut)', fontSize: 13 }}><Lock size={14} /> Modo estricto — termina tu enfoque</div>}
        </>
      )}

      {showNoise && (
        <>
          <div onClick={() => setShowNoise(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,23,48,0.4)' }} />
          <div className="card" style={{ position: 'fixed', top: 70, left: 20, width: 330, maxWidth: 'calc(100vw - 40px)', maxHeight: '72vh', overflowY: 'auto', borderRadius: 16, padding: 16, boxShadow: 'var(--shadow-lg)' }}>
            <WhiteNoisePanel />
          </div>
        </>
      )}
    </div>
  );
}

function topBtn(bg: string, color: string, side?: 'left'): React.CSSProperties {
  return {
    position: side ? 'absolute' : 'static', top: side ? 20 : undefined, left: side === 'left' ? 20 : undefined,
    width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, color,
  };
}

function ctrl(): React.CSSProperties {
  return { width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill)', color: 'var(--text-sec)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
}
